import React from "react";

import {
  Modal,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Avatar,
  IconButton,
  Divider,
  Paper,
  LinearProgress,
  Chip,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useGetPayoutsQuery } from "components/Slices/circleApiSlice";
import dayjs from "dayjs";
import { ImageAvatar } from "./ImageAvatar";

// Props interface
interface RotationModalProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
}

export default function RotationModal({
  open,
  onClose,
  groupId,
}: RotationModalProps) {
  // Mock engineer data

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    maxWidth: "95%",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 3,
    p: 0,
    outline: "none",
  };

  const { data: payouts, isLoading } = useGetPayoutsQuery(groupId);

  if (isLoading) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="engineer-rotation-modal-title"
        aria-describedby="engineer-rotation-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ p: 2 }}>
            Loading...
          </Typography>
        </Box>
      </Modal>
    );
  }

  // Calculate current rotation progress
  const monthlyComplete = payouts.filter(
    (payout: { status: string }) => payout.status === "Paid"
  ).length;
  const totalCount = payouts.filter(
    (payout: { status: string; isPayee: boolean }) =>
      payout.status !== "Delinquent" && payout.isPayee !== true
  ).length;
  const progressPercentage = (monthlyComplete / totalCount) * 100;

  const indexOfCurrentRotation =
    payouts.findIndex((payout: { isPayee: boolean }) => payout.isPayee) + 1;

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Paid":
        return "success";
      case "Processing":
        return "info";
      case "Pending":
        return "warning";
      case "Overdue":
        return "warning";
      case "Delinquent":
        return "delinquent";
      default:
        return "default";
    }
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case "Paid":
        return "Paid";
      case "Processing":
        return "Processing";
      case "Pending":
        return "Awaiting Payment";
      case "Overdue":
        return "Pending";
      case "Delinquent":
        return "Delinquent";
      default:
        return "Unknown";
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="engineer-rotation-modal-title"
      aria-describedby="engineer-rotation-modal-description"
    >
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            id="engineer-rotation-modal-title"
            variant="h6"
            component="h2"
          >
            Circle Rotation Schedule
          </Typography>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              id="engineer-rotation-modal-description"
              variant="body2"
            >
              Monthly payment progress: {monthlyComplete} of {totalCount}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              color="success"
              sx={{ width: "50%", height: 10, borderRadius: 5 }}
            />
          </Box>
          <Paper variant="outlined" sx={{ maxHeight: 500, overflow: "auto" }}>
            <List sx={{ p: 0 }}>
              {payouts.map((payout: any, index: number) => {
                const payoutOrder = index + 1;
                return (
                  <Box key={payout.id}>
                    <ListItem
                      sx={{
                        py: 1.5,
                        bgcolor:
                          payout.status === "Delinquent"
                            ? "rgb(162,78,78,.2)"
                            : payoutOrder <= indexOfCurrentRotation
                            ? "rgba(25, 118, 210, 0.08)"
                            : "transparent",
                        border: "2px dashed",
                        borderColor: payout.isPayee
                          ? "success.main"
                          : "transparent",
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={1}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              bgcolor:
                                payout.status === "Delinquent"
                                  ? "rgb(162,78,78,.25)"
                                  : payoutOrder <= indexOfCurrentRotation
                                  ? "success.main"
                                  : "grey.300",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {payout.status === "Delinquent" ? "-" : payoutOrder}
                          </Box>
                        </Grid>
                        <Grid item xs={8}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ImageAvatar
                              displayImg={payout.displayImg}
                              firstName={payout.firstName}
                            />
                            <Box sx={{ ml: 2 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  textDecoration:
                                    payout.status === "Delinquent"
                                      ? "line-through"
                                      : "none",
                                }}
                                noWrap
                              >
                                {`${payout.firstName} ${payout.lastName}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                noWrap
                                sx={{
                                  textDecoration:
                                    payout.status === "Delinquent"
                                      ? "line-through"
                                      : "none",
                                }}
                              >
                                {payout.email}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        {/* Rotation Date and Status */}
                        <Grid item xs={3}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {dayjs(String(payout.payoutDate)).format(
                                "MMM D, YYYY"
                              )}
                            </Typography>
                            {!payout.isPayee && (
                              <Chip
                                label={getStatusLabel(payout.status)}
                                color={getStatusColor(payout.status)}
                                size="small"
                                variant={payout.isPayee ? "filled" : "outlined"}
                              />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </ListItem>
                    {payoutOrder < payout.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </List>
          </Paper>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
